import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showError, showSuccess } from '../../services/alert';

const emptyMachine = {
  descricao: '',
  tipo: 'Trator',
  placa: '',
  horimetro: 0,
  status: 'Ativa',
  custoHora: 0,
  observacao: '',
};

const today = () => new Date().toISOString().substring(0, 10);

const Maquinas = () => {
  const navigate = useNavigate();
  const [maquinas, setMaquinas] = useState([]);
  const [safras, setSafras] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [form, setForm] = useState(emptyMachine);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [manutencao, setManutencao] = useState({
    maquinaId: '',
    safraId: '',
    data: today(),
    tipo: 'Preventiva',
    valor: 0,
    horimetro: 0,
    responsavel: '',
    observacao: '',
    gerarDespesa: true,
  });
  const [abastecimento, setAbastecimento] = useState({
    maquinaId: '',
    safraId: '',
    insumoId: '',
    data: today(),
    quantidadeLitros: 0,
    valorUnitario: 0,
    horimetro: 0,
    operador: '',
    observacao: '',
    baixarEstoque: true,
    gerarDespesa: true,
  });

  const maquinaSelecionada = useMemo(
    () => maquinas.find(item => String(item.id) === String(selectedId)),
    [maquinas, selectedId]
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const [maquinasRes, safrasRes, insumosRes] = await Promise.all([
        api.get('/maquinas'),
        api.get('/safras'),
        api.get('/insumos'),
      ]);
      setMaquinas(maquinasRes.data || []);
      setSafras(safrasRes.data || []);
      setInsumos(insumosRes.data || []);
    } catch (err) {
      console.error(err);
      showError('Erro ao carregar maquinas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!maquinaSelecionada) return;

    setForm({
      descricao: maquinaSelecionada.descricao || '',
      tipo: maquinaSelecionada.tipo || 'Trator',
      placa: maquinaSelecionada.placa || '',
      horimetro: maquinaSelecionada.horimetro || 0,
      status: maquinaSelecionada.status || 'Ativa',
      custoHora: maquinaSelecionada.custoHora || 0,
      observacao: maquinaSelecionada.observacao || '',
    });
    setManutencao(prev => ({ ...prev, maquinaId: maquinaSelecionada.id, horimetro: maquinaSelecionada.horimetro || 0 }));
    setAbastecimento(prev => ({ ...prev, maquinaId: maquinaSelecionada.id, horimetro: maquinaSelecionada.horimetro || 0 }));
  }, [maquinaSelecionada]);

  const changeForm = (event) => {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const normalizePayload = (data) => Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, value === '' ? null : value])
  );

  const saveMachine = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (selectedId) {
        await api.put(`/maquinas/${selectedId}`, form);
        showSuccess('Máquina atualizada com sucesso.');
      } else {
        await api.post('/maquinas', form);
        showSuccess('Máquina cadastrada com sucesso.');
      }
      setSelectedId('');
      setForm(emptyMachine);
      await loadData();
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || 'Erro ao salvar maquina.');
    } finally {
      setLoading(false);
    }
  };

  const submitEvent = async (event, type) => {
    event.preventDefault();
    const data = type === 'manutencao' ? manutencao : abastecimento;

    if (!data.maquinaId) {
      showError('Selecione uma maquina.');
      return;
    }

    setLoading(true);
    try {
      await api.post(type === 'manutencao' ? '/maquinas/manutencoes' : '/maquinas/abastecimentos', normalizePayload(data));
      showSuccess(type === 'manutencao' ? 'Manutenção registrada.' : 'Abastecimento registrado.');
      await loadData();
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || 'Erro ao registrar evento.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <Header />

      <div className="card shadow-lg border-0 rounded-3 mb-4">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-light btn-sm d-flex align-items-center gap-1"
              onClick={() => navigate('/menu')}
              disabled={loading}
            >
              <i className="bi bi-arrow-left text-success" />
              <span className="text-success fw-semibold">Voltar</span>
            </button>
            <div>
              <h5 className="mb-0">Máquinas e Implementos</h5>
              <small className="text-white-50">Custo por hora, manutenção, diesel e vínculo com a safra</small>
            </div>
          </div>
          <button className="btn btn-light text-success fw-bold" onClick={() => { setSelectedId(''); setForm(emptyMachine); }}>
            <i className="bi bi-plus-circle me-1" />
            Nova maquina
          </button>
        </div>
        <div className="card-body">
          <form className="row g-3" onSubmit={saveMachine}>
            <div className="col-md-4">
              <label className="form-label fw-semibold">Descrição *</label>
              <input className="form-control" name="descricao" value={form.descricao} onChange={changeForm} required />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Tipo *</label>
              <select className="form-select" name="tipo" value={form.tipo} onChange={changeForm}>
                <option>Trator</option>
                <option>Colheitadeira</option>
                <option>Pulverizador</option>
                <option>Caminhao</option>
                <option>Implemento</option>
                <option>Outro</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Placa</label>
              <input className="form-control" name="placa" value={form.placa} onChange={changeForm} />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Horímetro</label>
              <input className="form-control" type="number" step="0.01" name="horimetro" value={form.horimetro} onChange={changeForm} />
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Custo/hora</label>
              <CurrencyInput className="form-control" name="custoHora" value={form.custoHora} onChange={changeForm} placeholder="0,00" />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Status</label>
              <select className="form-select" name="status" value={form.status} onChange={changeForm}>
                <option value="Ativa">Ativa</option>
                <option value="Manutencao">Manutenção</option>
                <option value="Inativa">Inativa</option>
              </select>
            </div>
            <div className="col-md-9">
              <label className="form-label fw-semibold">Observação</label>
              <input className="form-control" name="observacao" value={form.observacao} onChange={changeForm} />
            </div>
            <div className="col-12 d-flex justify-content-end">
              <button className="btn btn-success" disabled={loading}>
                <i className="bi bi-save me-1" />
                {selectedId ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-lg-5">
          <div className="card shadow-sm border-0 rounded-3 h-100">
            <div className="card-header bg-light fw-semibold">Frota cadastrada</div>
            <div className="list-group list-group-flush">
              {maquinas.map(item => (
                <button
                  key={item.id}
                  className={`list-group-item list-group-item-action ${String(selectedId) === String(item.id) ? 'active' : ''}`}
                  onClick={() => setSelectedId(item.id)}
                >
                  <div className="d-flex justify-content-between">
                    <strong>{item.descricao}</strong>
                    <span>{item.status}</span>
                  </div>
                  <small>{item.tipo} | {Number(item.horimetro || 0).toLocaleString('pt-BR')} h | R$ {Number(item.custoHora || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/h</small>
                </button>
              ))}
              {!maquinas.length && <div className="p-3 text-muted">Nenhuma maquina cadastrada.</div>}
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow-sm border-0 rounded-3 mb-4">
            <div className="card-header bg-light fw-semibold">Manutenção</div>
            <div className="card-body">
              <form className="row g-3" onSubmit={(event) => submitEvent(event, 'manutencao')}>
                <SelectMaquina value={manutencao.maquinaId} maquinas={maquinas} onChange={value => setManutencao(prev => ({ ...prev, maquinaId: value }))} />
                <SelectSafra value={manutencao.safraId} safras={safras} onChange={value => setManutencao(prev => ({ ...prev, safraId: value }))} />
                <Field label="Data" type="date" value={manutencao.data} onChange={value => setManutencao(prev => ({ ...prev, data: value }))} />
                <Field label="Tipo" value={manutencao.tipo} onChange={value => setManutencao(prev => ({ ...prev, tipo: value }))} />
                <Field label="Valor" currency value={manutencao.valor} onChange={value => setManutencao(prev => ({ ...prev, valor: value }))} />
                <Field label="Horímetro" type="number" value={manutencao.horimetro} onChange={value => setManutencao(prev => ({ ...prev, horimetro: value }))} />
                <div className="col-md-8">
                  <label className="form-label">Responsavel</label>
                  <input className="form-control" value={manutencao.responsavel} onChange={e => setManutencao(prev => ({ ...prev, responsavel: e.target.value }))} />
                </div>
                <Check label="Gerar despesa" checked={manutencao.gerarDespesa} onChange={checked => setManutencao(prev => ({ ...prev, gerarDespesa: checked }))} />
                <div className="col-12 text-end">
                  <button className="btn btn-outline-success" disabled={loading}>Registrar manutenção</button>
                </div>
              </form>
            </div>
          </div>

          <div className="card shadow-sm border-0 rounded-3">
            <div className="card-header bg-light fw-semibold">Abastecimento</div>
            <div className="card-body">
              <form className="row g-3" onSubmit={(event) => submitEvent(event, 'abastecimento')}>
                <SelectMaquina value={abastecimento.maquinaId} maquinas={maquinas} onChange={value => setAbastecimento(prev => ({ ...prev, maquinaId: value }))} />
                <SelectSafra value={abastecimento.safraId} safras={safras} onChange={value => setAbastecimento(prev => ({ ...prev, safraId: value }))} />
                <div className="col-md-4">
                  <label className="form-label">Insumo diesel</label>
                  <select className="form-select" value={abastecimento.insumoId} onChange={e => setAbastecimento(prev => ({ ...prev, insumoId: e.target.value }))}>
                    <option value="">Sem baixa</option>
                    {insumos.map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}
                  </select>
                </div>
                <Field label="Litros" type="number" value={abastecimento.quantidadeLitros} onChange={value => setAbastecimento(prev => ({ ...prev, quantidadeLitros: value }))} />
                <Field label="Valor/L" currency value={abastecimento.valorUnitario} onChange={value => setAbastecimento(prev => ({ ...prev, valorUnitario: value }))} />
                <Field label="Data" type="date" value={abastecimento.data} onChange={value => setAbastecimento(prev => ({ ...prev, data: value }))} />
                <Field label="Horímetro" type="number" value={abastecimento.horimetro} onChange={value => setAbastecimento(prev => ({ ...prev, horimetro: value }))} />
                <div className="col-md-6">
                  <label className="form-label">Operador</label>
                  <input className="form-control" value={abastecimento.operador} onChange={e => setAbastecimento(prev => ({ ...prev, operador: e.target.value }))} />
                </div>
                <Check label="Baixar estoque" checked={abastecimento.baixarEstoque} onChange={checked => setAbastecimento(prev => ({ ...prev, baixarEstoque: checked }))} />
                <Check label="Gerar despesa" checked={abastecimento.gerarDespesa} onChange={checked => setAbastecimento(prev => ({ ...prev, gerarDespesa: checked }))} />
                <div className="col-12 text-end">
                  <button className="btn btn-outline-success" disabled={loading}>Registrar abastecimento</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', currency = false }) => (
  <div className="col-md-3">
    <label className="form-label">{label}</label>
    {currency
      ? <CurrencyInput className="form-control" value={value} onValueChange={onChange} placeholder="0,00" />
      : <input type={type} step={type === 'number' ? '0.01' : undefined} className="form-control" value={value} onChange={e => onChange(e.target.value)} />}
  </div>
);

const Check = ({ label, checked, onChange }) => (
  <div className="col-md-3 d-flex align-items-end">
    <div className="form-check">
      <input className="form-check-input" type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <label className="form-check-label">{label}</label>
    </div>
  </div>
);

const SelectMaquina = ({ value, maquinas, onChange }) => (
  <div className="col-md-6">
    <label className="form-label">Máquina</label>
    <select className="form-select" value={value} onChange={e => onChange(e.target.value)} required>
      <option value="">Selecione...</option>
      {maquinas.map(item => <option key={item.id} value={item.id}>{item.descricao}</option>)}
    </select>
  </div>
);

const SelectSafra = ({ value, safras, onChange }) => (
  <div className="col-md-6">
    <label className="form-label">Safra</label>
    <select className="form-select" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">Sem safra</option>
      {safras.map(item => <option key={item.id} value={item.id}>{item.cultura}</option>)}
    </select>
  </div>
);

export default Maquinas;
